import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { ChevronsUpDown, Check, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { financialsService } from '@/services/financials-graphql-service';
import type { CostCentre } from '@/types/financials';

interface CostCentrePickerProps {
  readonly value?: string;
  readonly displayValue?: string;
  readonly onValueChange: (code: string, name: string) => void;
  readonly projectCode?: string;
  readonly disabled?: boolean;
  readonly placeholder?: string;
  readonly className?: string;
  readonly costCentres?: CostCentre[];
  readonly loading?: boolean;
  readonly error?: boolean;
}

export function CostCentrePicker({
  value,
  displayValue,
  onValueChange,
  projectCode,
  disabled = false,
  placeholder = 'Select cost centre...',
  className,
  costCentres: externalCostCentres,
  loading: externalLoading,
  error: externalError,
}: Readonly<CostCentrePickerProps>) {
  const [internalCostCentres, setInternalCostCentres] = useState<CostCentre[]>([]);
  const [filteredCostCentres, setFilteredCostCentres] = useState<CostCentre[]>([]);
  const [internalLoading, setInternalLoading] = useState(false);
  const [internalError, setInternalError] = useState(false);
  const [open, setOpen] = useState(false);

  const useExternalData = externalCostCentres !== undefined;
  const costCentres = useExternalData ? externalCostCentres : internalCostCentres;
  const loading = useExternalData ? (externalLoading ?? false) : internalLoading;
  const error = useExternalData ? (externalError ?? false) : internalError;

  useEffect(() => {
    if (useExternalData) return;

    const fetchCostCentres = async () => {
      setInternalLoading(true);
      setInternalError(false);
      try {
        const data = await financialsService.getCostCentres();
        setInternalCostCentres(data);
      } catch (err) {
        console.error('Failed to fetch cost centres:', err);
        setInternalError(true);
      } finally {
        setInternalLoading(false);
      }
    };

    fetchCostCentres();
  }, [useExternalData]);

  useEffect(() => {
    if (projectCode) {
      const filtered = costCentres.filter(cc => cc.projectCode === projectCode);
      setFilteredCostCentres(filtered);
    } else {
      setFilteredCostCentres(costCentres);
    }
  }, [costCentres, projectCode]);

  const handleSelect = (costCentre: CostCentre) => {
    onValueChange(costCentre.code, costCentre.name);
    setOpen(false);
  };

  const getSelectedDisplay = () => {
    if (loading) {
      return (
        <span className="flex items-center">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Loading...
        </span>
      );
    }
    if (value) {
      const selectedCostCentre = costCentres.find(cc => cc.code === value);
      return selectedCostCentre
        ? `${selectedCostCentre.code} - ${selectedCostCentre.name}`
        : displayValue ?? value;
    }
    return placeholder;
  };

  if (disabled) {
    return (
      <Input
        value={displayValue ?? value ?? ''}
        readOnly
        className={cn('bg-muted', className)}
      />
    );
  }

  return (
    <div className={className}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between font-normal bg-white"
            disabled={loading}
          >
            {getSelectedDisplay()}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0" align="start">
          <Command>
            <CommandInput placeholder="Search cost centres..." />
            <CommandEmpty>No cost centre found.</CommandEmpty>
            <CommandGroup className="max-h-64 overflow-y-auto">
              {filteredCostCentres.map((costCentre) => {
                const uniqueKey = costCentre.projectCode 
                  ? `${costCentre.code}-${costCentre.projectCode}` 
                  : costCentre.code;
                return (
                  <CommandItem
                    key={uniqueKey}
                    value={`${costCentre.code} ${costCentre.name} ${costCentre.projectCode ?? ''}`}
                    onSelect={() => handleSelect(costCentre)}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === costCentre.code ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <span className="font-medium">{costCentre.code}</span>
                    <span className="ml-2 text-muted-foreground">{costCentre.name}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>
      {error && (
        <p className="text-sm text-destructive mt-1">Failed to load cost centres</p>
      )}
    </div>
  );
}
